import {useEffect, useState} from "react";
import {
    ArrowDown,
    ArrowUp,
    ExternalLink,
    FileImage,
    ImagePlus,
    Layers3,
    Plus,
    Trash2,
    Upload,
    X,
} from "lucide-react";
import {DataState} from "../../../shared/components/DataState";
import {ToolbarButton, ToolbarSelect} from "../../../shared/components/PageToolbar";
import {moveMediaItem, validateMediaFile} from "../contracts";
import {ConfirmDialog} from "./ConfirmDialog";
import {useTranslation, i18n} from "../../../shared/i18n";


const messageFor = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const useObjectUrl = (file) => {
    const [url, setUrl] = useState("");
    useEffect(() => {
        if (!file || !URL.createObjectURL) {
            setUrl("");
            return undefined;
        }
        const nextUrl = URL.createObjectURL(file);
        setUrl(nextUrl);
        return () => URL.revokeObjectURL?.(nextUrl);
    }, [file]);
    return url;
};

export const MediaWorkspace = ({
    locale,
    setLocale,
    localeOptions,
    bannersQuery,
    storiesQuery,
    canManage,
    mutations,
    result,
}) => {
    const {t} = useTranslation();
    const banners = bannersQuery.data?.banners || [];
    const stories = storiesQuery.data?.stories || [];
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [storyItemTarget, setStoryItemTarget] = useState(null);

    const move = (kind, item, direction) => {
        const source = kind === "banner" ? banners : stories;
        const next = moveMediaItem(source, item.id, direction).map(({id}) => ({id}));
        (kind === "banner" ? mutations.reorderBanner : mutations.reorderStory).mutate(next);
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        const mutation = deleteTarget.kind === "banner" ? mutations.deleteBanner : mutations.deleteStory;
        mutation.mutate({id: deleteTarget.item.id, locale}, {onSuccess: () => setDeleteTarget(null)});
    };
    const deletePending = mutations.deleteBanner.isPending || mutations.deleteStory.isPending;

    return (
        <div className="content-workspace">
            <div className="content-workspace-head">
                <div><h2>{t("sections.content.p26")}</h2><p>{t("sections.content.p222")}</p></div>
                <ToolbarSelect aria-label={t("sections.content.p223")} label={t("sections.content.p172")} value={locale} onChange={(event) => setLocale(event.target.value)}>
                    {localeOptions.map((item) => <option key={item} value={item}>{item.toUpperCase()}</option>)}
                </ToolbarSelect>
            </div>

            {result ? <p className={`content-result ${result.kind === "error" ? "is-error" : ""}`} role={result.kind === "error" ? "alert" : "status"}>{result.message}</p> : null}

            <div className="content-upload-grid">
                <MediaUploadCard
                    accept="image/*"
                    canManage={canManage}
                    fileLabel={t("sections.content.p224")}
                    icon={<ImagePlus size={17} />}
                    label={t("sections.content.p225")}
                    mutation={mutations.uploadBanner}
                    onUpload={(file) => mutations.uploadBanner.mutate({locale, file})}
                    submitLabel={t("sections.content.p226")}
                />
                <MediaUploadCard
                    accept="image/*"
                    canManage={canManage}
                    fileLabel={t("sections.content.p227")}
                    icon={<Layers3 size={17} />}
                    label={t("sections.content.p228")}
                    mutation={mutations.uploadStory}
                    onUpload={(file) => mutations.uploadStory.mutate({locale, file})}
                    submitLabel={t("sections.content.p229")}
                />
            </div>

            <MediaCollection
                canManage={canManage}
                empty={t("sections.content.p230")}
                error={bannersQuery.error}
                items={banners}
                kind="banner"
                loading={bannersQuery.isLoading}
                onDelete={(item) => setDeleteTarget({kind: "banner", item})}
                onMove={move}
                title={t("sections.content.p13")}
            />
            <MediaCollection
                canManage={canManage}
                empty={t("sections.content.p231")}
                error={storiesQuery.error}
                items={stories}
                kind="story"
                loading={storiesQuery.isLoading}
                onAddItem={setStoryItemTarget}
                onDelete={(item) => setDeleteTarget({kind: "story", item})}
                onMove={move}
                title="Stories"
            />

            {storyItemTarget ? (
                <StoryItemEditor
                    canManage={canManage}
                    mutation={mutations.uploadStoryItem}
                    onCancel={() => setStoryItemTarget(null)}
                    onComplete={() => setStoryItemTarget(null)}
                    story={storyItemTarget}
                />
            ) : null}

            <ConfirmDialog
                copy={deleteTarget ? t("sections.content.p232", {v0: deleteTarget.item.id, v1: locale.toUpperCase()}) : ""}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                open={Boolean(deleteTarget)}
                pending={deletePending}
                title={deleteTarget?.kind === "story" ? t("sections.content.p233") : t("sections.content.p234")}
            />
        </div>
    );
};

const MediaUploadCard = ({label, icon, fileLabel, submitLabel, accept, canManage, mutation, onUpload}) => {
    const {t} = useTranslation();
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const preview = useObjectUrl(file);
    const submit = () => {
        const validation = validateMediaFile(file);
        setError(validation);
        if (validation) return;
        onUpload(file);
    };

    return (
        <section className="content-upload-card">
            <div className="content-upload-copy"><span>{icon}</span><div><h3>{label}</h3><p>{file ? file.name : t("sections.content.p235")}</p></div></div>
            {preview ? <img alt={t("sections.content.p236", {v0: file.name})} className="content-upload-preview" src={preview} /> : <div className="content-upload-placeholder"><FileImage size={22} /><span>{t("sections.content.p237")}</span></div>}
            <label className="content-file-control">
                <span>{fileLabel}</span>
                <input accept={accept} aria-label={fileLabel} disabled={!canManage || mutation.isPending} type="file" onChange={(event) => {setFile(event.target.files?.[0] || null); setError("");}} />
            </label>
            {error ? <p className="content-field-error" role="alert">{error}</p> : null}
            {mutation.isError ? <p className="content-field-error" role="alert">{messageFor(mutation.error, t("sections.content.p238", {v0: label}))}</p> : null}
            <ToolbarButton aria-label={submitLabel} disabled={!canManage || mutation.isPending} onClick={submit}><Upload size={15} /> {mutation.isPending ? t("sections.content.p239") : submitLabel}</ToolbarButton>
        </section>
    );
};

const MediaCollection = ({title, kind, items, loading, error, empty, canManage, onMove, onDelete, onAddItem}) => (
    <section className="content-media-section">
        <div className="content-workspace-head"><div><h3>{title}</h3><p>{items.length} {i18n.t("sections.content.p240")}</p></div></div>
        {error ? <DataState title={i18n.t("sections.content.p241", {v0: title.toLowerCase()})} copy={messageFor(error, i18n.t("sections.content.p242"))} /> : loading ? (
            <div aria-label={i18n.t("sections.content.p243", {v0: title})} className="content-media-loading"><span /><span /><span /></div>
        ) : items.length ? (
            <div className="content-media-grid">
                {items.map((item, index) => (
                    <article className="content-media-card" key={item.id}>
                        <div className="content-media-image"><img alt={`${kind === "banner" ? i18n.t("sections.content.p225") : "Story"} ${item.id}`} src={item.image} /><span>#{index + 1}</span></div>
                        <div className="content-media-meta"><strong>{kind === "banner" ? i18n.t("sections.content.p225") : "Story"} #{item.id}</strong><span>{item.country} · {item.language?.toUpperCase()}</span></div>
                        <div className="content-media-actions">
                            <a aria-label={i18n.t("sections.content.p245", {v0: kind === "banner" ? i18n.t("sections.content.p244") : "story", v1: item.id})} href={item.image} rel="noreferrer" target="_blank"><ExternalLink size={14} /></a>
                            <button aria-label={i18n.t("sections.content.p246", {v0: kind === "banner" ? i18n.t("sections.content.p244") : "story", v1: item.id})} disabled={!canManage || index === 0} onClick={() => onMove(kind, item, "up")} type="button"><ArrowUp size={14} /></button>
                            <button aria-label={i18n.t("sections.content.p247", {v0: kind === "banner" ? i18n.t("sections.content.p244") : "story", v1: item.id})} disabled={!canManage || index === items.length - 1} onClick={() => onMove(kind, item, "down")} type="button"><ArrowDown size={14} /></button>
                            {kind === "story" ? <button aria-label={i18n.t("sections.content.p248", {v0: item.id})} disabled={!canManage} onClick={() => onAddItem(item)} type="button"><Plus size={14} /></button> : null}
                            <button aria-label={i18n.t("sections.content.p249", {v0: kind === "banner" ? i18n.t("sections.content.p244") : "story", v1: item.id})} disabled={!canManage} onClick={() => onDelete(item)} type="button"><Trash2 size={14} /></button>
                        </div>
                    </article>
                ))}
            </div>
        ) : <DataState title={i18n.t("sections.content.p250")} copy={empty} />}
    </section>
);

const StoryItemEditor = ({story, canManage, mutation, onCancel, onComplete}) => {
    const {t} = useTranslation();
    const [file, setFile] = useState(null);
    const [contentType, setContentType] = useState("image");
    const [redirectId, setRedirectId] = useState("");
    const [isLast, setIsLast] = useState(false);
    const [error, setError] = useState("");
    const preview = useObjectUrl(file);
    const submit = (event) => {
        event.preventDefault();
        const validation = validateMediaFile(file, {allowVideo: true});
        setError(validation);
        if (validation) return;
        mutation.mutate({storyId: story.id, file, contentType, redirectId, isLast}, {onSuccess: onComplete});
    };

    return (
        <form className="content-editor content-story-item-editor" onSubmit={submit}>
            <div className="content-workspace-head"><div><h3>{t("sections.content.p251")}{story.id}</h3><p>{t("sections.content.p252")}</p></div><button aria-label={t("sections.content.p253")} className="content-icon-button" disabled={mutation.isPending} onClick={onCancel} type="button"><X size={17} /></button></div>
            <div className="content-story-item-grid">
                <label className="content-file-control"><span>{t("sections.content.p254")}</span><input accept="image/*,video/*" aria-label={t("sections.content.p254")} disabled={!canManage || mutation.isPending} type="file" onChange={(event) => {setFile(event.target.files?.[0] || null); setError("");}} /></label>
                <label className="admin-field"><span>{t("sections.content.p255")}</span><select aria-label={t("sections.content.p255")} value={contentType} onChange={(event) => setContentType(event.target.value)}><option value="image">{t("sections.content.p256")}</option><option value="video">{t("sections.content.p257")}</option></select></label>
                <label className="admin-field"><span>{t("sections.content.p258")}</span><input aria-label={t("sections.content.p258")} value={redirectId} onChange={(event) => setRedirectId(event.target.value)} /></label>
                <label className="content-checkbox"><input aria-label={t("sections.content.p259")} checked={isLast} type="checkbox" onChange={(event) => setIsLast(event.target.checked)} /><span>{t("sections.content.p259")}</span></label>
            </div>
            {preview ? (file.type.startsWith("video/") ? <video className="content-story-preview" controls src={preview} /> : <img alt={t("sections.content.p236", {v0: file.name})} className="content-story-preview" src={preview} />) : null}
            {error ? <p className="content-field-error" role="alert">{error}</p> : null}
            {mutation.isError ? <p className="content-field-error" role="alert">{messageFor(mutation.error, t("sections.content.p260"))}</p> : null}
            <div className="content-editor-actions"><button className="admin-button admin-button-secondary" disabled={mutation.isPending} onClick={onCancel} type="button">{t("sections.content.p180")}</button><button className="admin-button admin-button-primary" disabled={!canManage || mutation.isPending} type="submit"><Upload size={15} /> {mutation.isPending ? t("sections.content.p239") : t("sections.content.p261")}</button></div>
        </form>
    );
};
